import React, { ReactNode, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { C, hexToRgba, prefersReducedMotion } from '../theme/tokens';

const PANEL_COUNT = 36;
const VB = 400; // fixed SVG coordinate space — the outer Box's rendered size is independent and responsive

// Max cursor-parallax travel for the ring, in px. The ring is the nearest,
// foreground object on the page — WormholeVoid's backdrop layers behind it
// (nebula/stars/constellations) use smaller offsets in far-to-near order
// (8/10/22px), so the ring, being closer still, correctly gets the
// largest offset of anything on screen. It's still small relative to the
// ring's own size (well under 5%), so this reads as depth/weight, never as
// the ring drifting away — the actual complaint that had to be fixed
// earlier was an unrelated rotation-pivot bug (see the doc comment below),
// not this bounded, elastic, cursor-anchored motion.
const PARALLAX_RING_PX = 34;
const PARALLAX_EASE = 0.07;

/**
 * The big ambient gate ring every pre-auth screen sits inside — modeled on
 * the Stargate Atlantis ring: a gunmetal band with a corrugated outer rim
 * and an inner annulus of small glyph panels, rotating continuously (the
 * one motion this component carries). There is no "complete" state; this
 * is atmosphere, not a progress indicator. `children` renders centered in
 * the void — content is always visible immediately, never gated behind a
 * loading wait. An event-horizon pulse (concentric rings expanding from
 * the ring outward) reinforces it as the page's focal point. `locking`
 * (driven by hover/focus on the page's own CTA) fills the glyph panels in
 * scattered order and pauses the ring's spin. A small cursor-parallax tilt
 * (bounded, elastic, always easing back toward center) sits on top of all
 * of that — same technique as `WormholeVoid`'s backdrop parallax, kept
 * self-contained here rather than shared, since the ring's offset is
 * deliberately the largest on the page and has nothing to do with stars.
 * Fully static under `prefers-reduced-motion` (spin, pulse, and parallax
 * all stop).
 *
 * BUG FIXED (kept for the next person who touches this): the glyph-panel
 * ring used to visibly sweep/drift away from center instead of spinning in
 * place. Root cause: the rotating `<g>` set `transformOrigin` as literal
 * pixels (`"200px 200px"`), which is ambiguous for an SVG element — some
 * browsers resolve a pixel-valued transform-origin against the element's
 * *rendered* CSS box rather than the SVG's own `viewBox` user-coordinate
 * space, so a render size other than exactly 400px throws the pivot off
 * center. `transform-origin: 50% 50%` is unambiguous and always resolves
 * to true center regardless of render size — that's the fix, and it's
 * unrelated to the parallax added afterward (which moves a different
 * element, the outer wrapper, via plain `translate`, not `rotate`).
 */
const WormholeGate: React.FC<{ accent?: string; locking?: boolean; children: ReactNode }> = ({
  accent = C.wormhole,
  locking = false,
  children,
}) => {
  const reduced = prefersReducedMotion();
  const wrapRef = useRef<HTMLDivElement>(null);
  const cx = VB / 2;
  const cy = VB / 2;

  const rTickIn = VB * 0.465;
  const rTickOut = VB * 0.485;
  const rBandOut = VB * 0.46;
  const rPanelOut = VB * 0.44;
  const rPanelIn = VB * 0.36;
  const rBandIn = VB * 0.345;

  useEffect(() => {
    if (reduced) return;
    const pointer = { targetX: 0, targetY: 0, currentX: 0, currentY: 0 };
    let raf = 0;

    const onPointerMove = (e: PointerEvent) => {
      pointer.targetX = Math.max(-1, Math.min(1, (e.clientX / window.innerWidth) * 2 - 1));
      pointer.targetY = Math.max(-1, Math.min(1, (e.clientY / window.innerHeight) * 2 - 1));
    };
    const onPointerOut = (e: PointerEvent) => {
      if (e.relatedTarget === null) {
        pointer.targetX = 0;
        pointer.targetY = 0;
      }
    };

    const tick = () => {
      pointer.currentX += (pointer.targetX - pointer.currentX) * PARALLAX_EASE;
      pointer.currentY += (pointer.targetY - pointer.currentY) * PARALLAX_EASE;
      if (wrapRef.current) {
        wrapRef.current.style.transform = `translate(${pointer.currentX * PARALLAX_RING_PX}px, ${pointer.currentY * PARALLAX_RING_PX}px)`;
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerout', onPointerOut);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerout', onPointerOut);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <Box
      ref={wrapRef}
      sx={{ position: 'relative', width: 'min(94vw, 760px)', aspectRatio: '1 / 1', flexShrink: 0, mx: 'auto' }}
    >
      {!reduced && (
        <Box aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                inset: '18%',
                borderRadius: '50%',
                border: `1px solid ${hexToRgba(accent, 0.4)}`,
                animation: `gateHorizonPulse 6s cubic-bezier(0.16,1,0.3,1) ${i * 2}s infinite`,
                '@keyframes gateHorizonPulse': {
                  '0%': { transform: 'scale(0.75)', opacity: 0 },
                  '18%': { opacity: 0.55 },
                  '100%': { transform: 'scale(1.85)', opacity: 0 },
                },
              }}
            />
          ))}
        </Box>
      )}
      <Box
        component="svg"
        viewBox={`0 0 ${VB} ${VB}`}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          filter: `drop-shadow(0 0 ${locking ? 40 : 30}px ${hexToRgba(accent, locking ? 0.34 : 0.22)})`,
          transition: 'filter 0.4s ease',
        }}
      >
        {/* Corrugated outer rim texture — fixed housing. */}
        {Array.from({ length: 72 }).map((_, i) => {
          const a = (i / 72) * Math.PI * 2;
          return (
            <line
              key={i}
              x1={cx + Math.cos(a) * rTickIn}
              y1={cy + Math.sin(a) * rTickIn}
              x2={cx + Math.cos(a) * rTickOut}
              y2={cy + Math.sin(a) * rTickOut}
              stroke={C.metalLight}
              strokeWidth={1}
            />
          );
        })}

        {/* Outer bezel — fixed housing. */}
        <circle cx={cx} cy={cy} r={rBandOut} fill="none" stroke={C.metalLight} strokeWidth={3} />

        {/* Glyph-panel ring — rotates continuously, the gate's one idle motion;
            pauses mid-spin while `locking` so the chevron fill below reads as
            the ring settling into a lock, not lighting up mid-drift.
            `transformOrigin: '50% 50%'` (not px) is what keeps the pivot
            locked to true center — see the component doc comment above. */}
        <g
          style={{
            transformOrigin: '50% 50%',
            animation: reduced ? 'none' : 'gatePanelSpin 130s linear infinite',
            animationPlayState: locking ? 'paused' : 'running',
          }}
        >
          <circle cx={cx} cy={cy} r={(rPanelOut + rPanelIn) / 2} fill="none" stroke={C.metalDark} strokeWidth={rPanelOut - rPanelIn} />
          {Array.from({ length: PANEL_COUNT }).map((_, i) => {
            const a = (i / PANEL_COUNT) * Math.PI * 2;
            const midR = (rPanelOut + rPanelIn) / 2;
            const px = cx + Math.cos(a) * midR;
            const py = cy + Math.sin(a) * midR;
            const deg = (a * 180) / Math.PI + 90;
            const w = ((2 * Math.PI * midR) / PANEL_COUNT) * 0.78;
            const h = rPanelOut - rPanelIn - 5;
            // Deterministic pseudo-random glyph glint per panel — a texture
            // detail, not literal show glyphs (36 hand-drawn symbols is
            // overkill for a background element viewers never zoom into).
            const seed = (i * 137.51) % 1;
            // A second, decorrelated pseudo-random value (golden-ratio step)
            // drives each panel's lock delay, so panels fill in scattered
            // order around the ring rather than marching index 0..35.
            const lockSeed = (i * 0.6180339887) % 1;
            const lockDelayMs = Math.round(lockSeed * 520);
            return (
              <g key={i} transform={`translate(${px} ${py}) rotate(${deg})`}>
                <rect
                  x={-w / 2}
                  y={-h / 2}
                  width={w}
                  height={h}
                  rx={1}
                  stroke={locking ? accent : C.metal}
                  strokeWidth={0.75}
                  style={{
                    fill: locking ? hexToRgba(accent, 0.9) : C.panelAlt,
                    filter: locking ? `drop-shadow(0 0 3px ${hexToRgba(accent, 0.85)})` : 'none',
                    transition: 'fill 0.3s ease, stroke 0.3s ease, filter 0.3s ease',
                    transitionDelay: `${lockDelayMs}ms`,
                  }}
                />
                <circle cx={(seed - 0.5) * w * 0.5} cy={0} r={1.3} fill={locking ? C.textOnFill : hexToRgba(C.cyan, 0.75)} />
              </g>
            );
          })}
        </g>

        {/* Inner bezel — fixed housing, the void boundary content sits inside. */}
        <circle cx={cx} cy={cy} r={rBandIn} fill="none" stroke={C.metalLight} strokeWidth={3} />

        <style>{`@keyframes gatePanelSpin { to { transform: rotate(360deg); } }`}</style>
      </Box>

      <Box sx={{ position: 'absolute', inset: '22%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        {children}
      </Box>
    </Box>
  );
};

export default WormholeGate;
